import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';

import { COMPONENT_TYPE, NoData } from 'modules/experiments/components/ui/emptyState';
import { EXPERIMENT_MONITOR_TABS } from 'modules/experiments/constants';
import {
  analyticsPageLoaded,
  analyticsPageOverviewTabLoaded,
  analyticsPagePerformanceTabLoaded,
} from 'modules/experiments/ducks/actions';
import {
  getCampaignMessageMedium,
  getExperimentHasError,
  getExperimentLoading,
} from 'modules/experiments/ducks/selectors';
import { useExperimentSearchParams, useExperimentsTranslations } from 'modules/experiments/hooks';
import { EXPERIMENT_ANALYTICS_TAB, SharedRouterState } from 'modules/experiments/types';
import { addPositiveToast } from 'modules/toastContainer';
import { TAB_ACTIONS, useRumAction } from 'services/observability/datadog';
import { ExperimentNotFound } from 'ui/illustrations/ExperimentNotFound';
import { IndexTabsList, NavTabTrigger, TabsGroup } from 'ui/tabsGroup';
import { getExperimentsIndexPageLink } from 'utils/appRoutes';

import {
  ContentWrapper,
  FullWidthPageContainer,
  PageBodySection,
  PageHeaderSection,
  StyledLoadingOverlay,
  TabBodySection,
  TabHeaderSection,
} from './Styled';
import { PageHeader } from './header';
import { ExperimentOverviewTab, ExperimentPerformanceTab } from './tabs';

export const ExperimentsMonitorPage = () => {
  const { t } = useExperimentsTranslations();
  const { endDateTime, experimentId, selectedTab, startDateTime, timezone, setSelectedTab } =
    useExperimentSearchParams();
  const { state, pathname, search } = useLocation<SharedRouterState>();
  const navigate = useNavigate();

  // Datadog
  const addSignal = useRumAction(TAB_ACTIONS);

  const onTabSelect = (tab: EXPERIMENT_ANALYTICS_TAB) => {
    addSignal('TAB_CHANGED', {
      selectedTab: tab,
    });
    setSelectedTab(tab);
  };

  // Redux
  const loading = useSelector(getExperimentLoading);
  const hasError = useSelector(getExperimentHasError);
  const messageMedium = useSelector(getCampaignMessageMedium);

  // Fetches experiment analytics data
  const dispatch = useDispatch();

  /**
   * Signals the redux state to start fetching the base page data
   * And to set the required 18n translation object
   */
  useEffect(() => {
    if (!experimentId) return;
    dispatch(analyticsPageLoaded(experimentId));
  }, [experimentId]);

  /** Load the overview tab data */
  useEffect(() => {
    if (!experimentId) return;

    dispatch(analyticsPageOverviewTabLoaded(experimentId));
  }, [experimentId]);

  /** Load the performance tab data */
  useEffect(() => {
    if (!experimentId || !messageMedium) return;

    const queryVariables = {
      id: experimentId,
      messageMedium,
      startTime: startDateTime,
      endTime: endDateTime,
    };
    dispatch(analyticsPagePerformanceTabLoaded(queryVariables));
  }, [
    experimentId,
    messageMedium,
    startDateTime?.toISOString(),
    endDateTime?.toISOString(),
    timezone,
  ]);

  // Show the launch toaster when the page redirects with an experiment name.
  // This means that an experiment was successfully launched.
  useEffect(() => {
    if (state?.experimentName) {
      const successToasterMessage = t('successMessage.launchExperiment', {
        experiment: state?.experimentName,
      });
      dispatch(addPositiveToast(successToasterMessage));
      // Delete the passed state.
      navigate(`${pathname}${search}`, {});
    }
  }, [state?.experimentName]);

  if (hasError) {
    return (
      <NoData
        testId="experiment-not-found-icon"
        componentType={COMPONENT_TYPE.TABLE}
        Icon={ExperimentNotFound}
        title={t('noData.monitorPage.title')}
        subTitle={t('noData.monitorPage.subTitle')}
        linkText={t('noData.monitorPage.link')}
        link={getExperimentsIndexPageLink()}
        postText={t('noData.monitorPage.postText')}
        newTabLink={false}
      />
    );
  }

  return (
    <FullWidthPageContainer>
      <PageHeaderSection>
        <ContentWrapper>
          <PageHeader />
        </ContentWrapper>
      </PageHeaderSection>
      <PageBodySection>
        <TabHeaderSection>
          <TabsGroup
            data-test="experiment-monitor-tabs"
            value={selectedTab}
            onValueChange={(newTab) => onTabSelect(newTab as EXPERIMENT_ANALYTICS_TAB)}
          >
            <IndexTabsList>
              {EXPERIMENT_MONITOR_TABS.map(({ id, label }) => (
                <NavTabTrigger
                  data-test={`experiment-monitor-${id}-tab-header`}
                  key={id}
                  value={id}
                >
                  {t(label)}
                </NavTabTrigger>
              ))}
            </IndexTabsList>
          </TabsGroup>
        </TabHeaderSection>
        <TabBodySection>
          {
            {
              [EXPERIMENT_ANALYTICS_TAB.OVERVIEW]: <ExperimentOverviewTab />,
              [EXPERIMENT_ANALYTICS_TAB.PERFORMANCE]: <ExperimentPerformanceTab />,
            }[selectedTab]
          }
        </TabBodySection>
      </PageBodySection>
      {loading && <StyledLoadingOverlay />}
    </FullWidthPageContainer>
  );
};
